import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Zoning, ZoningType } from '@popety-io/types'
import { isEnvelopeScored } from '@popety-io/utils'
import { ZoningOverridesService } from '../services/zoningOverridesService'
import { getSmartDefaultMetricSelection } from '../utils/zoningHelpers'

/**
 * Read a zone metric value with v1/v2.0 field pattern fallback.
 * Tries: {key}_max → {key}_max_value → {key}_value
 */
const getZoneMax = (zone: Zoning, key: string): number | undefined => {
  const z = zone as any
  return z[`${key}_max`] ?? z[`${key}_max_value`] ?? z[`${key}_value`]
}

export interface ZoningLandData {
  std?: number // Determining land area
  sbd?: number // Determining built area
  sb?: number // Built area
  spd?: number // Determining floor area
  sbpu?: number // Gross useful floor area
  sp?: number // Floor area
  vbr?: number // Built volume
  ios?: number // Land occupation index
  cos?: number // Land occupation coefficient
  ius?: number // Land use index
  cus?: number // Land use coefficient
  ibus?: number // Land use index (IBUS)
  mass_index?: number // Mass index
  max_height?: number // Maximum building height
  max_floor_nb?: number // Maximum number of floors
  unit_nb?: number // Number of units
  centerChProjection?: string // EPSG:2056 "x,y" — used to resolve geoportal URL templates
  geoCenter?: { lat: number; lon: number } | [number, number] | string // WGS84 geo_center from land entity
  geoPortalLink?: string // Pre-resolved geoportal link from Land entity (fallback when zone.geoportal_url missing)
  landGeoPolygon?: { type: string; coordinates: number[][][] | number[][][][] } // Land parcel polygon for map display

  // Regelbauweise envelope score (land-v5.0) — null on classic-indice lands
  indice_used?: string | null
  volume_envelope_authorized?: number | null
  volume_envelope_difference?: number | null
  buildable_area_setback?: number | null
  buildable_area_effective?: number | null
  setback_differentiated?: boolean | null
  building_length_capped?: boolean | null
  building_depth_capped?: boolean | null
  envelope_height_used?: string | null
}

export interface MetricGroup {
  group1: 'ios' | 'cos' | 'none'
  group2: 'ius' | 'cus' | 'ibus' | 'im' | 'none'
}

export interface UseZoningMetricsProps {
  zone: Zoning
  zoningType: ZoningType
  landId: string
  landData?: ZoningLandData
  zoningOverridesService?: ZoningOverridesService
}

export interface UseZoningMetricsReturn {
  // State
  isEditing: boolean
  pendingChanges: Record<string, number | null>
  overrides: Record<string, number>
  metricGroups: MetricGroup
  metricVisibility: Record<string, boolean>

  // Actions
  setIsEditing: (value: boolean) => void
  setPendingChanges: (value: Record<string, number | null>) => void
  setMetricGroups: (value: MetricGroup) => void
  handleMetricChange: (metricKey: string, value: number) => void
  handleMetricClear: (metricKey: string) => void
  handleMetricGroupChange: (group: 'group1' | 'group2', value: string) => void
  handleSave: () => Promise<void>
  handleCancel: () => void
  handleReset: () => Promise<void>

  // Computed
  hasOverrides: boolean
  hasPendingChanges: boolean
  getEffectiveValue: (
    metricKey: string,
    originalValue?: number
  ) => number | undefined
  isValueOverridden: (metricKey: string) => boolean
  isValuePending: (metricKey: string) => boolean
  isMetricVisible: (key: string, defaultVisible: boolean) => boolean
  toggleMetricVisibility: (key: string, defaultVisible: boolean) => void

  // Derived metrics calculations
  calculateDerivedSurface: (index?: number) => number | undefined
  getAuthorizedSbd: () => number | undefined
  getAuthorizedSb: () => number | undefined
  getAuthorizedSpd: () => number | undefined
  getAuthorizedSbpu: () => number | undefined
  getAuthorizedSp: () => number | undefined
  getAuthorizedVbr: () => number | undefined
}

/**
 * Hook for managing zoning metrics state and logic
 * Handles edit mode, metric overrides, exclusive groups, and derived metrics
 */
export const useZoningMetrics = ({
  zone,
  zoningType,
  landId,
  landData,
  zoningOverridesService,
}: UseZoningMetricsProps): UseZoningMetricsReturn => {
  // Get default group selections based on zone data. On envelope-scored
  // lands (Regelbauweise) the ratios are estimates that would contradict the
  // envelope score, so both groups default to 'none' — the user can still
  // opt back in (reference display) and that choice persists via the saved
  // metric_group_selections.
  const isEnvelope = isEnvelopeScored(landData?.indice_used)
  const defaultGroups = useMemo(
    () =>
      isEnvelope
        ? ({ group1: 'none', group2: 'none' } as MetricGroup)
        : getSmartDefaultMetricSelection(zone),
    [zone, isEnvelope]
  )
  const defaultGroupsRef = useRef(defaultGroups)
  defaultGroupsRef.current = defaultGroups

  // State
  const [isEditing, setIsEditing] = useState(false)
  const [pendingChanges, setPendingChanges] = useState<
    Record<string, number | null>
  >({})
  const [overrides, setOverrides] = useState<Record<string, number>>({})
  const [metricGroups, setMetricGroups] = useState<MetricGroup>(
    () => defaultGroupsRef.current
  )
  // Track saved group selections (what's persisted in backend)
  const [savedMetricGroups, setSavedMetricGroups] = useState<MetricGroup>(
    () => defaultGroupsRef.current
  )
  // Per-metric display state ({key: bool}, explicit booleans — deep-merge
  // backend). Keys: metric keys + 'group1' / 'group2' / 'envelope'.
  const [metricVisibility, setMetricVisibility] = useState<
    Record<string, boolean>
  >({})
  const [savedMetricVisibility, setSavedMetricVisibility] = useState<
    Record<string, boolean>
  >({})
  const [_isSaving, setIsSaving] = useState(false)

  // Keep the latest service in a ref so the load effect does not re-run every
  // time the service object is recreated. The app recreates it after a
  // value-edited save (the save refreshes landAnalysisUser → new valueEdited →
  // new service identity); re-running the load would reset the table and read
  // as a jarring "page refresh" on every save. We only need to reload when the
  // zone/type changes or the service first becomes available.
  const zoningOverridesServiceRef = useRef(zoningOverridesService)
  zoningOverridesServiceRef.current = zoningOverridesService
  const hasZoningOverridesService = !!zoningOverridesService

  // Load overrides from backend on mount
  useEffect(() => {
    const service = zoningOverridesServiceRef.current
    const loadOverrides = async () => {
      if (!service || !zone._id) return

      try {
        const data = await service.getZoningOverrides(
          landId,
          zone._id,
          zoningType
        )

        if (data) {
          setOverrides(data.metric_overrides || {})
          if (data.metric_group_selections) {
            const smartDefaults = defaultGroupsRef.current
            const loadedGroups = {
              group1:
                data.metric_group_selections.group1 || smartDefaults.group1,
              group2:
                data.metric_group_selections.group2 || smartDefaults.group2,
            }
            setMetricGroups(loadedGroups)
            setSavedMetricGroups(loadedGroups)
          }
          if (data.metric_visibility) {
            setMetricVisibility(data.metric_visibility)
            setSavedMetricVisibility(data.metric_visibility)
          }
        }
      } catch (error) {
        console.error('Failed to load zoning overrides:', error)
      }
    }

    loadOverrides()
    // Depend on service *presence*, not identity, to avoid reloading the table
    // when the app recreates the service after a save (see ref note above).
  }, [landId, zone._id, zoningType, hasZoningOverridesService])

  // Computed values
  const hasOverrides = useMemo(() => {
    return Object.keys(overrides).length > 0
  }, [overrides])

  // Check if group selections have changed from saved values
  const hasGroupChanges = useMemo(() => {
    return (
      metricGroups.group1 !== savedMetricGroups.group1 ||
      metricGroups.group2 !== savedMetricGroups.group2
    )
  }, [metricGroups, savedMetricGroups])

  const hasVisibilityChanges = useMemo(
    () =>
      JSON.stringify(metricVisibility) !==
      JSON.stringify(savedMetricVisibility),
    [metricVisibility, savedMetricVisibility]
  )

  const hasPendingChanges = useMemo(() => {
    return (
      Object.keys(pendingChanges).length > 0 ||
      hasGroupChanges ||
      hasVisibilityChanges
    )
  }, [pendingChanges, hasGroupChanges, hasVisibilityChanges])

  // Get effective value (pending > override > original)
  // null in pendingChanges means "remove this override" → revert to original
  const getEffectiveValue = useCallback(
    (metricKey: string, originalValue?: number): number | undefined => {
      if (metricKey in pendingChanges) {
        const pending = pendingChanges[metricKey]
        if (pending === null) return originalValue // Pending removal
        return pending
      }
      if (metricKey in overrides) {
        return overrides[metricKey]
      }
      return originalValue
    },
    [pendingChanges, overrides]
  )

  // Check if value is overridden (considering pending removals)
  const isValueOverridden = useCallback(
    (metricKey: string): boolean => {
      // If there's a pending removal, the value is no longer overridden
      if (metricKey in pendingChanges) {
        return pendingChanges[metricKey] !== null
      }
      return metricKey in overrides
    },
    [overrides, pendingChanges]
  )

  // Check if value has pending changes (excluding pending removals)
  const isValuePending = useCallback(
    (metricKey: string): boolean => {
      return metricKey in pendingChanges && pendingChanges[metricKey] !== null
    },
    [pendingChanges]
  )

  // Handle metric value change
  const handleMetricChange = useCallback((metricKey: string, value: number) => {
    setPendingChanges((prev) => ({
      ...prev,
      [metricKey]: value,
    }))
  }, [])

  // Handle clearing a metric override (revert to original value)
  const handleMetricClear = useCallback(
    (metricKey: string) => {
      if (metricKey in overrides) {
        // Mark for removal on save (null = remove this override)
        setPendingChanges((prev) => ({
          ...prev,
          [metricKey]: null,
        }))
      } else {
        // Just remove from pending changes
        setPendingChanges((prev) => {
          const next = { ...prev }
          delete next[metricKey]
          return next
        })
      }
    },
    [overrides]
  )

  // Per-metric display state. defaultVisible carries the caller's default
  // (populated metrics visible; envelope-land ratio concepts hidden; etc.)
  const isMetricVisible = useCallback(
    (key: string, defaultVisible: boolean): boolean =>
      metricVisibility[key] ?? defaultVisible,
    [metricVisibility]
  )

  const toggleMetricVisibility = useCallback(
    (key: string, defaultVisible: boolean) => {
      setMetricVisibility((prev) => ({
        ...prev,
        [key]: !(prev[key] ?? defaultVisible),
      }))
    },
    []
  )

  // Handle metric group selection change
  const handleMetricGroupChange = useCallback(
    (group: 'group1' | 'group2', value: string) => {
      setMetricGroups((prev) => ({
        ...prev,
        [group]: value as any,
      }))
    },
    []
  )

  // Save changes to backend
  const handleSave = useCallback(async () => {
    if (!zoningOverridesService || !zone._id) return

    setIsSaving(true)
    try {
      // Merge overrides with pending changes, then remove null entries (cleared overrides)
      const merged = { ...overrides, ...pendingChanges }
      const allOverrides: Record<string, number> = {}
      for (const [key, value] of Object.entries(merged)) {
        if (value !== null) {
          allOverrides[key] = value
        }
      }

      // Persist the exact selection, including 'none' (metric hidden). The
      // backend deep-merges value_edited, so omitting a group would keep its
      // previously saved value instead of clearing it — the hidden state must
      // be written explicitly or it reverts to the default on reload.
      const metricGroupSelectionsForBackend = {
        group1: metricGroups.group1,
        group2: metricGroups.group2,
      }

      await zoningOverridesService.saveZoningOverrides(
        landId,
        zone._id,
        zoningType,
        {
          metric_overrides: allOverrides,
          metric_group_selections: metricGroupSelectionsForBackend,
          metric_visibility: metricVisibility,
        }
      )

      // Update state
      setOverrides(allOverrides)
      setPendingChanges({})
      setSavedMetricGroups({ ...metricGroups })
      setSavedMetricVisibility({ ...metricVisibility })
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save zoning overrides:', error)
      throw error
    } finally {
      setIsSaving(false)
    }
  }, [
    zoningOverridesService,
    zone._id,
    landId,
    zoningType,
    overrides,
    pendingChanges,
    metricGroups,
    metricVisibility,
  ])

  // Cancel changes
  const handleCancel = useCallback(() => {
    setPendingChanges({})
    setMetricGroups({ ...savedMetricGroups })
    setMetricVisibility({ ...savedMetricVisibility })
    setIsEditing(false)
  }, [savedMetricGroups, savedMetricVisibility])

  // Reset to defaults (delete all overrides)
  const handleReset = useCallback(async () => {
    if (!zoningOverridesService || !zone._id) return

    setIsSaving(true)
    try {
      await zoningOverridesService.deleteZoningOverrides(
        landId,
        zone._id,
        zoningType
      )

      // Clear state and reset to defaults
      setOverrides({})
      setPendingChanges({})
      setMetricGroups({ ...defaultGroups })
      setSavedMetricGroups({ ...defaultGroups })
      setMetricVisibility({})
      setSavedMetricVisibility({})
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to reset zoning overrides:', error)
      throw error
    } finally {
      setIsSaving(false)
    }
  }, [zoningOverridesService, zone._id, landId, zoningType, defaultGroups])

  // Derived metrics calculations
  const calculateDerivedSurface = useCallback(
    (index?: number): number | undefined => {
      if (!landData?.std || index === undefined) return undefined
      return Math.round(landData.std * index)
    },
    [landData?.std]
  )

  const getAuthorizedSbd = useCallback((): number | undefined => {
    const iosMax = getEffectiveValue('ios_max', getZoneMax(zone, 'ios'))
    return calculateDerivedSurface(iosMax)
  }, [zone, getEffectiveValue, calculateDerivedSurface])

  const getAuthorizedSb = useCallback((): number | undefined => {
    const cosMax = getEffectiveValue('cos_max', getZoneMax(zone, 'cos'))
    return calculateDerivedSurface(cosMax)
  }, [zone, getEffectiveValue, calculateDerivedSurface])

  const getAuthorizedSpd = useCallback((): number | undefined => {
    const iusMax = getEffectiveValue('ius_max', getZoneMax(zone, 'ius'))
    return calculateDerivedSurface(iusMax)
  }, [zone, getEffectiveValue, calculateDerivedSurface])

  const getAuthorizedSbpu = useCallback((): number | undefined => {
    const cusMax = getEffectiveValue('cus_max', getZoneMax(zone, 'cus'))
    return calculateDerivedSurface(cusMax)
  }, [zone, getEffectiveValue, calculateDerivedSurface])

  const getAuthorizedSp = useCallback((): number | undefined => {
    const ibusMax = getEffectiveValue('ibus_max', getZoneMax(zone, 'ibus'))
    return calculateDerivedSurface(ibusMax)
  }, [zone, getEffectiveValue, calculateDerivedSurface])

  const getAuthorizedVbr = useCallback((): number | undefined => {
    const imMax = getEffectiveValue('im_max', getZoneMax(zone, 'im'))
    return calculateDerivedSurface(imMax)
  }, [zone, getEffectiveValue, calculateDerivedSurface])

  return {
    // State
    isEditing,
    pendingChanges,
    overrides,
    metricGroups,
    metricVisibility,

    // Actions
    setIsEditing,
    setPendingChanges,
    setMetricGroups,
    handleMetricChange,
    handleMetricClear,
    handleMetricGroupChange,
    handleSave,
    handleCancel,
    handleReset,

    // Computed
    hasOverrides,
    hasPendingChanges,
    getEffectiveValue,
    isValueOverridden,
    isValuePending,
    isMetricVisible,
    toggleMetricVisibility,

    // Derived metrics
    calculateDerivedSurface,
    getAuthorizedSbd,
    getAuthorizedSb,
    getAuthorizedSpd,
    getAuthorizedSbpu,
    getAuthorizedSp,
    getAuthorizedVbr,
  }
}
